import { FC } from 'react';
import { Button, Flex, Typography } from 'antd';
import { TbHistory } from 'react-icons/tb';

import { useResourceHistory } from '@/contexts/02-Authenticated';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { AttachmentDestination } from '@/services';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import MiddleDot from '@/shared/components/typography/MiddleDot';

import { DocumentIntelligence } from '../AttachmentRedactionEditor/documentIntelligence';
import { historyResourceTypeForAttachmentDestination } from '../attachmentResource';

interface AttachmentDocumentReviewHeaderProps {
    destination?: AttachmentDestination;
    intelligence?: DocumentIntelligence;
    loading?: boolean;
}

const AttachmentDocumentReviewHeader: FC<AttachmentDocumentReviewHeaderProps> = ({
    destination,
    intelligence,
    loading = false,
}) => {
    const { token } = useAntDesignTheme();
    const { openResourceHistory } = useResourceHistory();
    const metadata = loading
        ? ['Analyzing context…']
        : intelligence
          ? [
                `${intelligence.wordCount.toLocaleString()} words`,
                `${intelligence.concepts.length.toLocaleString()} ${intelligence.concepts.length === 1 ? 'concept' : 'concepts'}`,
                `Ontology v${intelligence.ontologyVersion}`,
            ]
          : [];

    return (
        <Flex
            align='center'
            justify='space-between'
            style={{
                borderBottom: `1px solid ${token.colorBorder}`,
                flex: 'none',
                gap: '0.75rem',
                minHeight: '2.75rem',
                padding: '0.375rem 0.75rem',
            }}
        >
            <Flex align='center' style={{ minWidth: 0, overflow: 'hidden' }}>
                <Typography.Text strong style={{ flex: 'none' }}>
                    File Review
                </Typography.Text>
                {metadata.map((item) => (
                    <Flex key={item} align='center' style={{ minWidth: 0 }}>
                        <MiddleDot horizontalMargin='0.5rem' styles={{ flex: 'none' }} />
                        <Typography.Text
                            type='secondary'
                            style={{ fontSize: token.fontSizeSM, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                            {item}
                        </Typography.Text>
                    </Flex>
                ))}
            </Flex>
            {destination ? (
                <SmallTooltip title={`Show ${destination.resourceName} History`} placement='bottomRight'>
                    <Button
                        icon={<TbHistory style={{ color: token.colorTextTertiary }} />}
                        size='small'
                        style={{ flex: 'none' }}
                        type='text'
                        onClick={() =>
                            openResourceHistory({
                                resourceId: destination.resourceId,
                                resourceType: historyResourceTypeForAttachmentDestination(destination),
                                title: destination.resourceName,
                            })
                        }
                    >
                        Show History
                    </Button>
                </SmallTooltip>
            ) : null}
        </Flex>
    );
};

export default AttachmentDocumentReviewHeader;
